/**
 * Health profile (F2.4–F2.6): HR zones (versioned, append-only), Training
 * Level history, and 1RM history per exercise — under `/v2/stats`. Writing
 * for a client (trainer with consent) goes to `…/client/:clientId`; GET and
 * DELETE are always self.
 */
import type {
  ApiSetTrainingLevelDto,
  ApiCreateOneRmDto,
  ApiSetHrZonesDto,
} from '@mfw/common';

import type {
  ApiTrainingLevelEntry,
  FetchHrZonesAtArgs,
  ApiHrZoneVersion,
  ApiOneRmEntry,
} from './types/statsEntries.types';

import { destroy, post, get } from './axios';

// ——— HR zones ———

// closes the current version and opens a new one; returns the NEW version
export const setHrZones = async ({
  clientId,
  ...dto
}: { clientId?: string } & ApiSetHrZonesDto) => {
  const url = clientId
    ? `/v2/stats/hr-zones/client/${clientId}`
    : '/v2/stats/hr-zones';
  const response = await post<ApiHrZoneVersion>(url, dto);
  // BE returns the raw entity, but the `post` helper types a `{data}` wrapper
  return (response ?? null) as ApiHrZoneVersion | null;
};

// without `at` → version history (newest first)
export const fetchHrZones = async () => {
  const response = await get<ApiHrZoneVersion[]>('/v2/stats/hr-zones');
  return response ?? null;
};

// the version valid on the given day — null if none existed on that day
export const fetchHrZonesAt = async (args: FetchHrZonesAtArgs) => {
  const response = await get<ApiHrZoneVersion | null>('/v2/stats/hr-zones', {
    query: { at: args.at },
  });
  return response ?? null;
};

// ——— Training Level ———

export const setTrainingLevel = async ({
  clientId,
  ...dto
}: ApiSetTrainingLevelDto & { clientId?: string }) => {
  const url = clientId
    ? `/v2/stats/training-level/client/${clientId}`
    : '/v2/stats/training-level';
  const response = await post<ApiTrainingLevelEntry>(url, dto);
  return (response ?? null) as ApiTrainingLevelEntry | null;
};

// history (newest first); the first row = the current level
export const fetchTrainingLevel = async () => {
  const response = await get<ApiTrainingLevelEntry[]>(
    '/v2/stats/training-level',
  );
  return response ?? null;
};

// ——— 1RM ———

export const createOneRm = async ({
  clientId,
  ...dto
}: { clientId?: string } & ApiCreateOneRmDto) => {
  const url = clientId
    ? `/v2/stats/one-rm/client/${clientId}`
    : '/v2/stats/one-rm';
  const response = await post<ApiOneRmEntry>(url, dto);
  return (response ?? null) as ApiOneRmEntry | null;
};

export const deleteOneRm = async ({ id }: { id: string }) => {
  await destroy(`/v2/stats/one-rm/${id}`);
};
